import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import config from '../../config';

const WaitlistModalContext = createContext({
  openWaitlistModal: () => {},
});

const schema = yup.object().shape({
  name: yup.string().trim().required('Full name is required'),
  email: yup.string().trim().email('Please enter a valid email address').required('Email address is required'),
});

const defaultMessages = {
  success: 'You have joined the wish list. We will be in touch soon.',
  error: 'There was a problem submitting your application. Please try again.',
};

const getErrorMessage = async (response) => {
  try {
    const payload = await response.json();
    const firstFieldError = payload?.errors ? Object.values(payload.errors).flat()[0] : null;

    return firstFieldError || payload?.message || defaultMessages.error;
  } catch (error) {
    return defaultMessages.error;
  }
};

const WaitlistModal = ({ isOpen, onClose }) => {
  const [responseMessage, setResponseMessage] = useState(null);
  const [responseType, setResponseType] = useState(null);
  const closeTimerRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
    },
  });

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeModal = useCallback(() => {
    clearCloseTimer();
    onClose();
  }, [clearCloseTimer, onClose]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setResponseMessage(null);
      setResponseType(null);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeModal, isOpen]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const onSubmit = async (data) => {
    clearCloseTimer();
    setResponseMessage(null);
    setResponseType(null);

    try {
      const response = await fetch(`${config.endpoints.baseUrl}/api/waitlist-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name.trim(),
          email: data.email.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      setResponseMessage(defaultMessages.success);
      setResponseType('success');
      reset();
      closeTimerRef.current = window.setTimeout(onClose, 1800);
    } catch (error) {
      setResponseMessage(error?.message || defaultMessages.error);
      setResponseType('error');
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="bl-waitlist-modal" role="presentation">
      <button className="bl-waitlist-modal__backdrop" type="button" aria-label="Close waitlist modal" onClick={closeModal} />
      <div className="bl-waitlist-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="waitlist-modal-title">
        <button className="bl-waitlist-modal__close" type="button" aria-label="Close waitlist modal" onClick={closeModal}>
          <i className="fas fa-times" aria-hidden="true" />
        </button>

        <div className="bl-waitlist-modal__header">
          <h2 id="waitlist-modal-title">Apply to Wish List</h2>
          <p>
            Be the first to know when full access opens.<br />
            Your data, personalized insights.
          </p>
        </div>

        <form className="bl-waitlist-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <label>
            <span>Full Name <b aria-hidden="true">*</b></span>
            <input type="text" placeholder="Your full name" autoComplete="name" {...register('name')} />
            {errors.name ? <small>{errors.name.message}</small> : null}
          </label>

          <label>
            <span>Email Address <b aria-hidden="true">*</b></span>
            <input type="email" placeholder="name@example.com" autoComplete="email" {...register('email')} />
            {errors.email ? <small>{errors.email.message}</small> : null}
          </label>

          {responseMessage ? (
            <div className={`bl-waitlist-form__message bl-waitlist-form__message--${responseType}`}>
              {responseMessage}
            </div>
          ) : null}

          <button className="bl-waitlist-form__submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Applying...' : 'Apply to Wish List'}
          </button>
          <button className="bl-waitlist-form__cancel" type="button" onClick={closeModal}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
};

export const WaitlistModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openWaitlistModal = useCallback(() => setIsOpen(true), []);
  const closeWaitlistModal = useCallback(() => setIsOpen(false), []);

  return (
    <WaitlistModalContext.Provider value={{ openWaitlistModal }}>
      {children}
      <WaitlistModal isOpen={isOpen} onClose={closeWaitlistModal} />
    </WaitlistModalContext.Provider>
  );
};

export const useWaitlistModal = () => useContext(WaitlistModalContext);
